// The registration form's values, their checks, and how they become the API request
// (plan §6.4). Pure functions, so every rule is unit-tested without rendering.
import {
  identifierLooksLike,
  MAX_ESTIMATED_AGE_YEARS,
  normalisePhone,
  validateEstimatedAge,
  type EstimatedAgeErrorCode,
  type IdentifierSystem,
  type PhoneErrorCode,
} from '@wodi/domain';
import {
  IDENTIFIER_SYSTEMS,
  type OptionalRequiredField,
  type ValueSetConceptView,
} from '@wodi/shared';
import { PHONE_ERROR_MESSAGES } from '@wodi/ui';

import type { RegisterPatientInput } from '../api/patients.ts';

export const IDENTIFIER_LABELS: Record<IdentifierSystem, string> = {
  cr_id: 'CR ID',
  national_id: 'National ID',
  birth_certificate: 'Birth certificate',
  passport: 'Passport',
  alien_id: 'Alien ID',
  refugee_id: 'Refugee ID',
  legacy_patient_number: 'Old patient number',
};

/** Identifier types reception may add; the CR ID comes from the national registry. */
export const RECEPTION_IDENTIFIER_SYSTEMS = IDENTIFIER_SYSTEMS.filter((s) => s !== 'cr_id');

export interface IdentifierRow {
  system: IdentifierSystem | '';
  value: string;
  issuingCountry: string;
}

export interface PrincipalRef {
  id: string;
  displayName: string;
  patientNumber: string;
}

export interface RegistrationValues {
  /** Made once per registration and kept in the draft, so a retry can't register twice. */
  clientRequestId: string;
  given: string;
  middle: string;
  family: string;
  /** The chosen sex concept's ID from the tenant's value set. */
  sexId: string;
  birthKind: 'exact' | 'estimated';
  birthDate: string | null;
  ageValue: string;
  ageUnit: 'years' | 'months';
  identifiers: IdentifierRow[];
  /** Stored form (2547…/2541…), or null when empty or not valid yet. */
  phone: string | null;
  phoneError: PhoneErrorCode | null;
  phoneOwner: 'patient' | 'someone_else';
  county: string;
  subCounty: string;
  ward: string;
  locality: string;
  kinGiven: string;
  kinFamily: string;
  kinPhone: string | null;
  kinPhoneError: PhoneErrorCode | null;
  kinRelationshipId: string;
  dependantOf: PrincipalRef | null;
  dependantRelationshipId: string;
  consentDecision: 'granted' | 'refused' | 'not_asked' | '';
  consentTextId: string;
}

export function emptyRegistration(clientRequestId: string): RegistrationValues {
  return {
    clientRequestId,
    given: '',
    middle: '',
    family: '',
    sexId: '',
    birthKind: 'exact',
    birthDate: null,
    ageValue: '',
    ageUnit: 'years',
    identifiers: [],
    phone: null,
    phoneError: null,
    phoneOwner: 'patient',
    county: '',
    subCounty: '',
    ward: '',
    locality: '',
    kinGiven: '',
    kinFamily: '',
    kinPhone: null,
    kinPhoneError: null,
    kinRelationshipId: '',
    dependantOf: null,
    dependantRelationshipId: '',
    consentDecision: '',
    consentTextId: '',
  };
}

/**
 * Starts the form from what was searched (plan §6.4): a phone goes to Phone, something
 * that looks like a document number to a new ID row, words to the names.
 */
export function prefillFromSearch(values: RegistrationValues, text: string): RegistrationValues {
  const trimmed = text.trim();
  if (trimmed === '') return values;
  const phone = normalisePhone(trimmed);
  if (phone.ok) return { ...values, phone: phone.value, phoneError: null };
  if (identifierLooksLike(trimmed) !== null) {
    return { ...values, identifiers: [{ system: '', value: trimmed, issuingCountry: '' }] };
  }
  const words = trimmed.split(/\s+/);
  if (words.length === 1) return { ...values, given: words[0] ?? '' };
  return {
    ...values,
    given: words[0] ?? '',
    middle: words.slice(1, -1).join(' '),
    family: words.at(-1) ?? '',
  };
}

export const ESTIMATED_AGE_MESSAGES: Record<EstimatedAgeErrorCode, string> = {
  'estimated_age.not_whole_number': 'Enter the age as a whole number.',
  'estimated_age.use_months': 'For a child under 2, enter the age in months.',
  'estimated_age.use_years': 'From 2 years old, enter the age in years.',
  'estimated_age.too_old': `An estimated age can be at most ${String(MAX_ESTIMATED_AGE_YEARS)} years. Check the age.`,
};

export type RegistrationErrors = Partial<Record<string, string>>;

export interface ValidationContext {
  requiredFields: readonly OptionalRequiredField[];
  /** Consent wording is configured, so the question must be answered. */
  consentAvailable: boolean;
  /** Today's East Africa date, `YYYY-MM-DD`. */
  today: string;
}

function kinStarted(v: RegistrationValues): boolean {
  return (
    v.kinGiven.trim() !== '' ||
    v.kinFamily.trim() !== '' ||
    v.kinPhone !== null ||
    v.kinPhoneError !== null ||
    v.kinRelationshipId !== ''
  );
}

/** Every problem with the form, keyed by field, in the words shown at that field. */
export function validateRegistration(
  v: RegistrationValues,
  ctx: ValidationContext,
): RegistrationErrors {
  const errors: RegistrationErrors = {};
  const required = new Set(ctx.requiredFields);

  if (v.given.trim() === '') errors['given'] = "Enter the patient's first name.";
  if (required.has('family_name') && v.family.trim() === '') {
    errors['family'] = 'Enter the family name.';
  }
  if (v.sexId === '') errors['sexId'] = "Choose the patient's sex.";

  if (v.birthKind === 'exact') {
    if (v.birthDate === null) {
      errors['birthDate'] = 'Enter the date of birth, or choose Estimated age.';
    } else if (v.birthDate > ctx.today) {
      errors['birthDate'] = "The date of birth can't be in the future.";
    }
  } else {
    const value = Number(v.ageValue.trim());
    if (v.ageValue.trim() === '' || !Number.isFinite(value)) {
      errors['ageValue'] = 'Enter the estimated age.';
    } else {
      const checked = validateEstimatedAge({ unit: v.ageUnit, value });
      if (!checked.ok) errors['ageValue'] = ESTIMATED_AGE_MESSAGES[checked.code];
    }
  }

  v.identifiers.forEach((row, index) => {
    if (row.system === '') errors[`identifiers.${String(index)}.system`] = 'Choose the type of ID.';
    if (row.value.trim() === '') {
      errors[`identifiers.${String(index)}.value`] = 'Enter the number, or remove this ID.';
    }
    if (row.system === 'passport' && !/^[A-Za-z]{2}$/.test(row.issuingCountry.trim())) {
      errors[`identifiers.${String(index)}.issuingCountry`] =
        'Enter the issuing country as a two-letter code, like KE.';
    }
  });
  if (required.has('identifier') && v.identifiers.length === 0) {
    errors['identifiers'] = 'Add at least one ID document.';
  }

  if (v.phoneError !== null && v.phoneError !== 'phone.empty') {
    errors['phone'] = PHONE_ERROR_MESSAGES[v.phoneError];
  } else if (required.has('phone') && v.phone === null) {
    errors['phone'] = PHONE_ERROR_MESSAGES['phone.empty'];
  }

  if (
    required.has('residence') &&
    v.county.trim() === '' &&
    v.subCounty.trim() === '' &&
    v.ward.trim() === '' &&
    v.locality.trim() === ''
  ) {
    errors['county'] = 'Enter where the patient lives.';
  }

  if (kinStarted(v) || required.has('next_of_kin')) {
    if (v.kinGiven.trim() === '') errors['kinGiven'] = "Enter the next of kin's first name.";
    if (v.kinRelationshipId === '') {
      errors['kinRelationshipId'] = 'Choose how they are related to the patient.';
    }
    if (v.kinPhoneError !== null && v.kinPhoneError !== 'phone.empty') {
      errors['kinPhone'] = PHONE_ERROR_MESSAGES[v.kinPhoneError];
    }
  }

  if (v.dependantOf !== null && v.dependantRelationshipId === '') {
    errors['dependantRelationshipId'] = 'Choose how the patient is related to them.';
  }

  if (ctx.consentAvailable && v.consentDecision === '') {
    errors['consentDecision'] = 'Record what the patient decided.';
  }
  return errors;
}

function textOrNull(text: string): string | null {
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
}

function freeText(text: string) {
  const display = textOrNull(text);
  return display === null ? null : { system: null, code: null, display };
}

function codedRef(concepts: readonly ValueSetConceptView[], id: string) {
  const concept = concepts.find((c) => c.id === id);
  if (concept === undefined) throw new Error('Unknown value set concept');
  return { system: concept.system, code: concept.code };
}

export function birthInput(v: RegistrationValues): RegisterPatientInput['birth'] | null {
  if (v.birthKind === 'exact') {
    return v.birthDate === null ? null : { kind: 'exact', birthDate: v.birthDate };
  }
  const value = Number(v.ageValue.trim());
  if (v.ageValue.trim() === '' || !Number.isInteger(value)) return null;
  return { kind: 'estimated', age: { unit: v.ageUnit, value } };
}

export function completeIdentifiers(v: RegistrationValues) {
  return v.identifiers.flatMap((row) =>
    row.system === '' || row.value.trim() === ''
      ? []
      : [
          {
            system: row.system,
            value: row.value.trim(),
            issuingCountry:
              row.system === 'passport' ? row.issuingCountry.trim().toUpperCase() : null,
          },
        ],
  );
}

/** The duplicate check's request for the form as it stands (plan §4). */
export function duplicateCheckInput(v: RegistrationValues) {
  return {
    name: { given: v.given.trim(), middle: textOrNull(v.middle), family: textOrNull(v.family) },
    birth: birthInput(v),
    identifiers: completeIdentifiers(v),
    phones: v.phone === null ? [] : [v.phone],
    principalPatientId: v.dependantOf?.id ?? null,
  };
}

/** The registration request. Call only after `validateRegistration` found nothing. */
export function toRegisterRequest(
  v: RegistrationValues,
  concepts: { sex: readonly ValueSetConceptView[]; relationship: readonly ValueSetConceptView[] },
  override: { candidateIds: string[]; reason: string } | null,
): RegisterPatientInput {
  const birth = birthInput(v);
  if (birth === null) throw new Error('Registration is missing a date of birth or age');
  const residence = {
    county: freeText(v.county),
    subCounty: freeText(v.subCounty),
    ward: freeText(v.ward),
    locality: textOrNull(v.locality),
  };
  const hasResidence = Object.values(residence).some((part) => part !== null);
  return {
    clientRequestId: v.clientRequestId,
    name: { given: v.given.trim(), middle: textOrNull(v.middle), family: textOrNull(v.family) },
    sex: codedRef(concepts.sex, v.sexId),
    birth,
    residence: hasResidence ? residence : null,
    identifiers: completeIdentifiers(v),
    contacts:
      v.phone === null
        ? []
        : [{ kind: 'mobile', value: v.phone, belongsTo: v.phoneOwner, isPrimary: true }],
    nextOfKin:
      v.kinGiven.trim() === ''
        ? []
        : [
            {
              relatedPatientId: null,
              relatedPerson: {
                givenName: v.kinGiven.trim(),
                familyName: textOrNull(v.kinFamily),
                phone: v.kinPhone,
              },
              relationship: codedRef(concepts.relationship, v.kinRelationshipId),
            },
          ],
    dependantOf:
      v.dependantOf === null
        ? null
        : {
            patientId: v.dependantOf.id,
            relationship: codedRef(concepts.relationship, v.dependantRelationshipId),
          },
    consent:
      v.consentDecision === 'granted' || v.consentDecision === 'refused'
        ? {
            purpose: 'hie_data_sharing',
            decision: v.consentDecision,
            method: 'verbal',
            consentTextId: v.consentTextId,
          }
        : null,
    duplicateOverride: override,
  };
}
